import type { WsEvent } from '@fixflow/shared-types';
import { apiGet, apiPost } from './client.js';

export type DisplayConnectionStatus = { connected: boolean; connectedAt?: string };

const reviewDebounceMs = 200;
const pendingReviewByStore = new Map<string, ReturnType<typeof setTimeout>>();
let reviewGeneration = 0;

function isIdleUpdate(event: WsEvent): boolean {
  return (
    event.type === 'display.update' &&
    typeof event.payload === 'object' &&
    event.payload !== null &&
    (event.payload as { mode?: string }).mode === 'idle'
  );
}

export function getDisplayConnectionStatus(storeId: string) {
  return apiGet<DisplayConnectionStatus>(`/api/v1/display/${storeId}/status`).then((r) => r.data);
}

export function createCustomerDisplaySession() {
  return apiPost<{ sessionToken: string }>('/api/v1/display/customer-session', {}).then((r) => r.data);
}

export function broadcastDisplayIdle(storeId: string): Promise<void> {
  return broadcastToDisplay(storeId, {
    type: 'display.update',
    payload: { mode: 'idle' },
    timestamp: new Date().toISOString(),
  });
}

/** Staff → API → WebSocket: push any display event (cart mirror, draft ticket, idle). Best-effort. */
export async function broadcastToDisplay(storeId: string, event: WsEvent): Promise<void> {
  const send = async () => {
    try {
      await apiPost<{ success: boolean }, WsEvent>(`/api/v1/display/${storeId}/broadcast`, event);
    } catch (err) {
      console.warn('[Display] Broadcast failed:', err);
    }
  };

  if (isIdleUpdate(event)) {
    const pending = pendingReviewByStore.get(storeId);
    if (pending) {
      clearTimeout(pending);
      pendingReviewByStore.delete(storeId);
    }
    reviewGeneration += 1;
    await send();
    return;
  }

  const gen = ++reviewGeneration;
  const pending = pendingReviewByStore.get(storeId);
  if (pending) clearTimeout(pending);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pendingReviewByStore.delete(storeId);
      if (gen !== reviewGeneration) {
        resolve();
        return;
      }
      void send().finally(resolve);
    }, reviewDebounceMs);
    pendingReviewByStore.set(storeId, timer);
  });
}

export function createWaiverDisplayToken(
  ticketId: string,
  body: { waiverType: 'pre_repair' | 'post_repair' },
) {
  return apiPost<{ displayToken: string }, typeof body>(
    `/api/v1/tickets/${ticketId}/waiver/display-token`,
    body,
  ).then((r) => r.data);
}
